import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useQuery } from "@tanstack/react-query";
import { Check, ArrowRight, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import * as api from "./briefingService";

/**
 * BriefingSuccess — peak-end after completing the briefing. While Claude builds
 * the draft flow it shows the transcription.lottie and a real elapsed timer. We
 * deliberately avoid fake "step X of Y" progress: the generation is a single
 * model call, so showing invented sub-steps would misrepresent what's actually
 * happening. It polls a SEPARATE query key so it never writes the shared
 * ["briefing", clientId] cache (which would flip status→completed and unmount
 * this screen).
 *
 * Self-healing: "ready" is driven by the generated flow actually existing
 * (`generatedFlowId`), not only by `generationStatus`. So even if the API
 * restarts mid-generation and never writes "done", the screen unblocks once the
 * flow is there.
 */
export default function BriefingSuccess({ clientId, onDone }) {
  const reduceMotion = useReducedMotion();
  const [elapsed, setElapsed] = useState(0);

  const { data } = useQuery({
    queryKey: ["briefing-generation", clientId],
    queryFn: () => api.fetchBriefing(clientId),
    enabled: !!clientId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const b = query.state.data;
      const ready = b?.generationStatus === "done" || !!b?.generatedFlowId;
      const failed = b?.generationStatus === "error" && !b?.generatedFlowId;
      return ready || failed ? false : 3000;
    },
  });

  const ready = data?.generationStatus === "done" || !!data?.generatedFlowId;
  const error = data?.generationStatus === "error" && !data?.generatedFlowId;
  const generating = !ready && !error;
  const slow = generating && elapsed >= 150; // reassure / offer escape if it drags

  // Real elapsed timer while generating (no fabricated step progress).
  useEffect(() => {
    if (!generating) return undefined;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [generating]);

  return (
    <div className="relative flex min-h-[70vh] items-center justify-center overflow-hidden px-4">
      <div
        aria-hidden="true"
        className={cn(
          "tw-blob-cyan absolute left-1/2 top-1/3 h-80 w-80 -translate-x-1/2",
          !reduceMotion && "animate-float-soft",
        )}
      />

      <motion.section
        initial={{ opacity: 0, y: reduceMotion ? 0 : 16, scale: reduceMotion ? 1 : 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="tw-glass relative z-10 w-full max-w-md rounded-[28px] p-8 text-center sm:p-10"
      >
        {error ? (
          <>
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-8 w-8" />
            </div>
            <h2 className="mt-6 text-2xl font-semibold tracking-tight text-foreground">
              Briefing concluído
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Recebemos tudo. A montagem automática do fluxo falhou desta vez.
              Nossa equipe finaliza o rascunho para você. Você já pode seguir
              para o painel.
            </p>
          </>
        ) : ready ? (
          <>
            <motion.div
              initial={{ scale: reduceMotion ? 1 : 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 320, damping: 18 }}
              className={cn(
                "mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-cyan-600 text-white",
                !reduceMotion && "animate-pulse-glow-cyan",
              )}
            >
              <Check className="h-8 w-8" strokeWidth={2.5} />
            </motion.div>
            <h2 className="mt-6 text-2xl font-semibold tracking-tight text-foreground">
              Seu fluxo está pronto!
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Geramos um rascunho do seu atendimento e triagem com IA. Vamos
              revisar juntos. Você não monta do zero.
            </p>
          </>
        ) : (
          <>
            <div className="mx-auto h-40 w-40">
              <DotLottieReact
                src="/assets/transcription.lottie"
                loop
                autoplay
                style={{ width: "100%", height: "100%" }}
              />
            </div>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-foreground">
              Montando seu fluxo com IA…
            </h2>
            <p className="mt-3 text-sm text-muted-foreground">
              {slow
                ? "Está levando um pouco mais que o normal. Pode seguir para o painel; o rascunho aparece lá assim que ficar pronto."
                : "Estou lendo o briefing e desenhando o fluxo de atendimento e triagem do seu escritório."}
            </p>
            <p className="mt-2 text-xs font-medium tabular-nums text-cyan-600 dark:text-cyan-400">
              {elapsed}s · geralmente leva de 1 a 2 minutos
            </p>
          </>
        )}

        <Button
          size="lg"
          onClick={onDone}
          disabled={generating && !slow}
          className="mt-8 w-full hover:rounded-2xl"
        >
          {ready ? "Ver meu fluxo" : error || slow ? "Ir para o painel" : `Aguarde… ${elapsed}s`}
          {(ready || error || slow) && <ArrowRight className="h-4 w-4" />}
        </Button>
      </motion.section>
    </div>
  );
}
